import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Calendar, Compass, Waves as WavesIcon, ShieldCheck, Users, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1493225255756-d9584f8606e9?w=2000";

const ABILITY_OPTIONS = [
  { v: "complete_beginner", l: "Complete beginner" },
  { v: "beginner", l: "Beginner" },
  { v: "improving_beginner", l: "Improving beginner" },
  { v: "lower_intermediate", l: "Lower intermediate" },
  { v: "intermediate", l: "Intermediate" },
];

function today() {
  return new Date().toISOString().slice(0, 10);
}

function useDefaultLocation() {
  const [coords, setCoords] = useState(null);
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setCoords({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
      () => {},
      { enableHighAccuracy: false, timeout: 5000 },
    );
  }, []);
  return coords;
}

export default function Home() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [locationLabel, setLocationLabel] = useState("");
  const [date, setDate] = useState(today());
  const [ability, setAbility] = useState(user?.profile?.ability || "beginner");
  const [maxTravel, setMaxTravel] = useState(90);
  const [popular, setPopular] = useState([]);
  const geo = useDefaultLocation();

  useEffect(() => {
    api.get("/surf-areas?limit=6").then((r) => setPopular(r.data.items || [])).catch(() => {});
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const params = new URLSearchParams({
      date,
      max_travel_minutes: String(maxTravel),
      ability,
    });
    if (locationLabel) params.set("place", locationLabel);
    if (geo && !locationLabel) {
      params.set("lat", String(geo.lat));
      params.set("lon", String(geo.lon));
    }
    navigate(`/recommendations?${params.toString()}`);
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `linear-gradient(180deg, rgba(0,58,67,0.35), rgba(0,58,67,0.55)), url(${HERO_IMG})` }}
          aria-hidden
        />
        <div className="absolute inset-0 grain-overlay" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-5 md:px-8 pt-14 md:pt-24 pb-16 md:pb-28">
          <div className="max-w-2xl">
            <p className="text-sand-100/90 text-xs uppercase tracking-[0.25em] font-semibold" data-testid="hero-strap">
              Find your best surf
            </p>
            <h1
              className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05] tracking-tight"
              data-testid="hero-title"
            >
              Where should you surf,{" "}
              <span className="italic text-[#F4C7B7]">for who you are today?</span>
            </h1>
            <p className="mt-5 text-sand-100/90 text-base md:text-lg max-w-xl" data-testid="hero-sub">
              Surfmatics reads your ability, location and available time — then reads the ocean.
              You get one clear recommendation, not a spreadsheet of waves and wind.
            </p>
          </div>

          {/* Search card */}
          <form
            onSubmit={onSubmit}
            data-testid="home-search-form"
            className="mt-8 md:mt-12 bg-white/95 backdrop-blur border border-white/40 rounded-2xl p-4 md:p-5 shadow-[0_20px_60px_rgba(0,58,67,0.25)]"
          >
            <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3">
              <label className="flex items-center gap-2 rounded-xl bg-sand-100 border border-border px-3 h-11">
                <MapPin className="w-4 h-4 text-teal-800" />
                <input
                  data-testid="home-location-input"
                  className="flex-1 bg-transparent outline-none text-sm text-teal-900 placeholder:text-muted-foreground"
                  placeholder={geo ? "Using your location — or type a place" : "Where are you starting from?"}
                  value={locationLabel}
                  onChange={(e) => setLocationLabel(e.target.value)}
                />
              </label>
              <label className="flex items-center gap-2 rounded-xl bg-sand-100 border border-border px-3 h-11">
                <Calendar className="w-4 h-4 text-teal-800" />
                <input
                  data-testid="home-date-input"
                  type="date"
                  className="flex-1 bg-transparent outline-none text-sm text-teal-900"
                  value={date}
                  min={today()}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <div>
                <Select value={ability} onValueChange={setAbility}>
                  <SelectTrigger
                    data-testid="home-ability-select"
                    className="h-11 rounded-xl bg-sand-100 border-border text-sm"
                  >
                    <SelectValue placeholder="Your ability" />
                  </SelectTrigger>
                  <SelectContent>
                    {ABILITY_OPTIONS.map((o) => (
                      <SelectItem key={o.v} value={o.v} data-testid={`ability-${o.v}`}>
                        {o.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Select value={String(maxTravel)} onValueChange={(v) => setMaxTravel(Number(v))}>
                  <SelectTrigger
                    data-testid="home-travel-select"
                    className="h-11 rounded-xl bg-sand-100 border-border text-sm"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[30, 60, 90, 120, 180].map((m) => (
                      <SelectItem key={m} value={String(m)} data-testid={`travel-${m}`}>
                        {m} min max drive
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="submit"
                data-testid="home-search-submit"
                className="h-11 px-6 rounded-xl bg-terracotta-500 text-white font-medium text-sm hover:bg-terracotta-600 flex items-center justify-center gap-2"
              >
                <Search className="w-4 h-4" />
                Find my best surf
              </button>
            </div>
            <div className="mt-3 text-[11px] text-muted-foreground">
              We combine live marine and weather forecasts with community reports. Tide data is a
              deterministic estimate and clearly labelled.
            </div>
          </form>
        </div>
      </section>

      {/* Why */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: <WavesIcon className="w-6 h-6" />,
              t: "Personal, not generic",
              d: "The recommendation reflects your ability, board, wave tolerance and crowd preference — not the biggest, punchiest wave for the day.",
            },
            {
              icon: <ShieldCheck className="w-6 h-6" />,
              t: "Safety-aware by design",
              d: "Hard exclusions block unsafe recommendations — overhead surf for a beginner, dangerous winds, or high rip current risk.",
            },
            {
              icon: <Users className="w-6 h-6" />,
              t: "Community reports",
              d: "Recent local reports raise or lower confidence, and always sit next to the forecast for context.",
            },
          ].map((f, i) => (
            <div
              key={f.t}
              className="card-soft p-6 md:p-8 animate-fade-up"
              style={{ animationDelay: `${i * 80}ms` }}
              data-testid={`home-feature-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-sand-200 text-teal-800 flex items-center justify-center">
                {f.icon}
              </div>
              <h3 className="mt-5 font-display text-xl text-teal-900">{f.t}</h3>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Popular */}
      <section className="max-w-6xl mx-auto px-5 md:px-8 pb-20">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="overline">Curated surf areas</div>
            <h2 className="font-display text-3xl md:text-4xl text-teal-900 mt-2">
              Places we currently cover
            </h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {popular.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => navigate(`/surf-area/${a.slug}`)}
              className="text-left card-soft overflow-hidden group"
              data-testid={`popular-${a.slug}`}
            >
              {a.image_url && (
                <div
                  className="h-32 bg-cover bg-center"
                  style={{ backgroundImage: `url(${a.image_url})` }}
                />
              )}
              <div className="p-5">
                <div className="overline text-[10px]">{a.country} · {a.region}</div>
                <div className="font-display text-lg text-teal-900 mt-1 group-hover:underline underline-offset-4">
                  {a.name}
                </div>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{a.short_description}</p>
                <div className="mt-3 text-xs text-teal-800 font-semibold flex items-center gap-1">
                  View forecast <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
